import { getWebsiteUrl } from '@va/dashboard/selectors/core';
import { useGetDynamicPages, useWebsiteStaticPagesInfiniteScroll } from '@va/dashboard/util-hooks';
import { LinkIcon } from '@va/icons';
import { DynamicPageType, PageTypeEnum, StaticPageType } from '@va/types/website';
import { IconWithBackground } from '@va/ui/design-system';
import { getDynamicPageUrl, getWellFormedUrl } from '@va/util/helpers';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

export const useWebsitePagesOptions = () => {
  const websiteUrl = useSelector(getWebsiteUrl);

  const { data: dynamicPagesList = [] } = useGetDynamicPages();

  const {
    staticPages: staticPagesList,
    lastElementRef,
    onSearch: onPageSearch,
    searchValue,
    clearSearchField,
    isValidating,
  } = useWebsiteStaticPagesInfiniteScroll();

  const dynamicPagesOptions = useMemo(
    () => dynamicPagesList.map((page) => formatWebsitePageForDropdown(page, websiteUrl)),
    [dynamicPagesList, websiteUrl],
  );

  const staticPagesOptions = useMemo(
    () => staticPagesList.map((page) => formatWebsitePageForDropdown(page, websiteUrl)),
    [staticPagesList, websiteUrl],
  );

  const websitePagesOptions = searchValue ? staticPagesOptions : dynamicPagesOptions.concat(staticPagesOptions); // Filtering is only done for static pages

  return {
    websitePagesOptions,
    dynamicPagesOptions,
    staticPagesOptions,
    staticPagesList,
    lastElementRef,
    onPageSearch,
    clearSearchField,
    searchValue,
    isValidating,
  };
};

export function formatWebsitePageForDropdown(page: DynamicPageType | StaticPageType, websiteUrl: string) {
  const isDynamic = !!(page as DynamicPageType).pattern;

  const name = isDynamic ? (page as DynamicPageType).name : (page as StaticPageType).title;
  const url = isDynamic
    ? getDynamicPageUrl((page as DynamicPageType).pattern, websiteUrl)
    : (page as StaticPageType).url;

  return {
    id: page.key,
    value: url,
    label: name,
    smallLabel: url,
    icon: (
      <IconWithBackground
        className='!bg-white-snow group-hover:!bg-white'
        icon={() => <LinkIcon color='#696969' />}
        onClick={(event) => {
          window.open(getWellFormedUrl(url), '_blank');
          event.stopPropagation();
        }}
      />
    ),
    type: isDynamic ? PageTypeEnum.dynamic : PageTypeEnum.static,
    pattern: isDynamic ? (page as DynamicPageType).pattern : undefined,
  };
}
